import React, { useCallback, useEffect, useState, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import { Col, Row, Modal, Badge } from 'react-bootstrap';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import TextInput from 'components/inputs/Input';
import { FiEdit2, FiEye, FiArchive } from 'react-icons/fi';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { UPDATE_STATUS_CODE, DELETE_STATUS_CODE, MANAGE_WALLETS } from 'components/common/constant';
import { FETCH_WALLET_TOP_UP_OFFERS, DELETE_WALLET_TOP_UP_OFFER, FETCH_WALLET_TOP_UP_OFFER_HISTORY } from 'actions/walletTopUpOffer';
import { formatOfferDate, mutedText, renderOfferStatus, archiveEndError } from 'view/sessionLevelOffer/SessionLevelOfferList';

export const getTopUpOfferStatus = (item) => {
  const now = moment();
  const ended = _.get(item, 'expire_at') && moment(item.expire_at).isBefore(now);
  if (ended && _.get(item, 'archived_at')) return 'Archived';
  if (ended) return 'Expired';
  if (_.get(item, 'start_date') && moment(item.start_date).isAfter(now)) return 'Scheduled';
  return 'Active';
};

const getRecordStatus = (slabs) => {
  const statuses = _.map(slabs, getTopUpOfferStatus);
  return _.find(['Active', 'Scheduled', 'Expired'], (s) => _.includes(statuses, s)) || 'Archived';
};

const summarize = (slabs) => ({
  count: _.size(slabs),
  minValue: _.min(_.map(slabs, 'min_value')),
  maxValue: _.max(_.map(slabs, 'max_value')),
  start: _.minBy(slabs, (s) => +new Date(s.start_date))?.start_date,
  end: _.maxBy(slabs, (s) => +new Date(s.expire_at))?.expire_at,
});

const WalletTopUpOfferList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigateTo } = useContext(NavContext);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveStart, setArchiveStart] = useState('');
  const [archiveEnd, setArchiveEnd] = useState('');

  const liveOffers = useSelector((state) => state.walletTopUpOffer.walletTopUpOffers);
  const history = useSelector((state) => state.walletTopUpOffer.walletTopUpOfferHistory);
  const isLoader = useSelector((state) => state.walletTopUpOffer.isLoading);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const profileData = useSelector((state) => state.profile.userProfile);
  const currentUserName = _.get(profileData, 'name', '');
  const canManage = _.some(_.get(rolePermissionData, '[0].permissions', []), { name: MANAGE_WALLETS });

  const records = [
    ...(_.isEmpty(liveOffers) ? [] : [{ isLive: true, key_name: 'live', key_value: liveOffers }]),
    ..._.map(history, (r) => ({ ...r, isLive: false })),
  ];

  const getOffers = useCallback(() => {
    dispatch({ type: FETCH_WALLET_TOP_UP_OFFERS.REQUEST });
    dispatch({ type: FETCH_WALLET_TOP_UP_OFFER_HISTORY.REQUEST });
  }, []);

  useEffect(() => {
    getOffers();
  }, []);

  const archiveRecord = useCallback(() => {
    dispatch({
      type: DELETE_WALLET_TOP_UP_OFFER.REQUEST,
      payload: { start_date: moment(archiveStart).toISOString(), end_date: moment(archiveEnd).toISOString() },
      cb: (res) => {
        if (_.get(res, 'status') === DELETE_STATUS_CODE || _.get(res, 'status') === UPDATE_STATUS_CODE) {
          setShowArchive(false);
          getOffers();
        }
      },
    });
  }, [archiveStart, archiveEnd]);

  const endError = archiveEndError(archiveStart, archiveEnd);

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={t('header.walletTopUpOffers', 'Wallet Top-up Offers')} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <div className="page-content-wrapper scrollable">
            <div className="user-group-page_main">
              <div className="user-group-main-block">
                <Card>
                  <div className="user-group-block">
                    <div className="user-group-search-box">
                      <Row className="align-items-center">
                        <Col xl={8}>
                          <span style={mutedText}>
                            Only the live record is shown in the app. Adding a new offer moves the current live record to old records.
                          </span>
                        </Col>
                        <Col xl={4} className="d-flex justify-content-end">
                          {canManage && (
                            <Button className="user-group-btn" onClick={() => navigateTo('/addWalletTopUpOffer')}>
                              {t('button.addOffer', 'Add Offer')}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </div>
                    <div className="user-group-table">
                      <div className="table-responsive">
                        <table className="record-list-table">
                          <thead>
                            <tr>
                              <th className="px-3">Record</th>
                              <th className="px-3">Slabs</th>
                              <th className="px-3">Amount Range</th>
                              <th className="px-3">Valid</th>
                              <th className="px-3">Status</th>
                              <th className="px-3">Last Updated</th>
                              <th className="px-3 action-col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {isLoader ? (
                              <tr>
                                <td colSpan={7} className="border-0">
                                  <Skeleton height={15} />
                                </td>
                              </tr>
                            ) : _.isEmpty(records) ? (
                              <tr>
                                <td colSpan={7} className="border-0">
                                  <div className="empty-data-block">
                                    <div className="mb-3">No offers found</div>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              _.map(records, (record) => {
                                const sum = summarize(record.key_value);
                                return (
                                  <tr key={record.key_name}>
                                    <td className="px-3">
                                      {record.isLive ? 'Live record' : `Old record ${record.copy_no}`}
                                      <div style={mutedText}>{record.isLive ? 'wallet_top_up_offers' : record.key_name}</div>
                                    </td>
                                    <td className="px-3">{sum.count}</td>
                                    <td className="px-3">
                                      ₹{sum.minValue ?? 0} - ₹{sum.maxValue ?? 0}
                                    </td>
                                    <td className="px-3">
                                      {formatOfferDate(sum.start)}
                                      <div style={mutedText}>to {formatOfferDate(sum.end)}</div>
                                    </td>
                                    <td className="px-3">
                                      {record.isLive ? (
                                        renderOfferStatus(getRecordStatus(record.key_value))
                                      ) : (
                                        <Badge pill bg="secondary">
                                          Replaced
                                        </Badge>
                                      )}
                                    </td>
                                    <td className="px-3">{formatOfferDate(_.max(_.map(record.key_value, 'updatedAt')))}</td>
                                    <td className="px-3">
                                      {record.isLive ? (
                                        <React.Fragment>
                                          <span
                                            className="user-group-table-editIcon"
                                            title={canManage ? 'Edit slabs' : 'View slabs'}
                                            onClick={() => navigateTo('/editWalletTopUpOffer')}
                                          >
                                            {canManage ? <FiEdit2 /> : <FiEye />}
                                          </span>
                                          {canManage && (
                                            <span
                                              className="user-group-table-deleteIcon"
                                              title="Archive record"
                                              onClick={() => {
                                                setShowArchive(true);
                                                setArchiveStart('');
                                                setArchiveEnd('');
                                              }}
                                            >
                                              <FiArchive />
                                            </span>
                                          )}
                                        </React.Fragment>
                                      ) : (
                                        <span
                                          className="user-group-table-editIcon"
                                          title="View slabs"
                                          onClick={() => navigateTo(`/viewWalletTopUpOffer/${record.key_name}`)}
                                        >
                                          <FiEye />
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </SkeletonTheme>
        {showArchive && (
          <Modal show centered size="lg" onHide={() => setShowArchive(false)}>
            <div className="delete-modal-main_page">
              <div className="delete-modal--inner">
                <div className="delete-modal---block_title">
                  <div className="inner--block_title">
                    <div className="title">Archive Record</div>
                    <p className="sub-title">
                      Running slabs get these dates (never past their own expiry), keep running until the end date, then show as Archived.
                    </p>
                    <Row>
                      <Col md={6}>
                        <TextInput
                          isRequired
                          type="datetime-local"
                          label="Start Date"
                          value={archiveStart}
                          onChange={(e) => setArchiveStart(e.target.value)}
                        />
                      </Col>
                      <Col md={6}>
                        <TextInput
                          isRequired
                          type="datetime-local"
                          label="End Date"
                          value={archiveEnd}
                          min={archiveStart || undefined}
                          onChange={(e) => setArchiveEnd(e.target.value)}
                          error={endError}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="delete-modal-input-box__block">
                  <Row>
                    <Col lg={6}>
                      <Button className="delete-model--btn cancel--btn" onClick={() => setShowArchive(false)}>
                        {t('button.cancel', 'Cancel')}
                      </Button>
                    </Col>
                    <Col lg={6}>
                      <Button
                        className="delete-model--btn remove--btn"
                        onClick={archiveRecord}
                        disabled={!archiveStart || !archiveEnd || !!endError || isLoader}
                      >
                        Archive
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          </Modal>
        )}
        {currentUserName === 'CZ Tenant Admin' ? null : <Footer />}
      </LayoutContainer>
    </React.Fragment>
  );
};

export default WalletTopUpOfferList;
